namespace $.$$ {

	type $shm_knowledge_manual_document = { id: string, title: string, content: string, parent_id?: string, editable?: boolean, revision?: string }

	export class $shm_knowledge_manual extends $.$shm_knowledge_manual {

		// Keep drafts independent of the mounted editor and its disposable fibers.
		private editor_contents = new Map<string, string>()
		private editor_baselines = new Map<string, $shm_knowledge_manual_editor_document | null>()

		@ $mol_mem_key
		editor_draft( id: string, next?: string ) {
			if( next !== undefined ) this.editor_contents.set( id, next )
			return this.editor_contents.get( id ) ?? ''
		}

		@ $mol_mem_key
		editor_baseline( id: string, next?: $shm_knowledge_manual_editor_document | null ) {
			if( next !== undefined ) this.editor_baselines.set( id, next )
			return this.editor_baselines.get( id ) ?? null
		}

		@ $mol_mem
		documents() {
			const url = new URL( this.api_url() )
			url.searchParams.set( 'knowledge', this.kind() )
			url.searchParams.set( 'revision', String( this.documents_revision() ) )
			const data = this.$.$mol_fetch.json( url.toString(), {
				headers: this.api_headers(), cache: 'no-store',
			} ) as { documents?: $shm_knowledge_manual_document[], error?: string }
			if( data.error ) throw new Error( data.error )
			return data.documents ?? []
		}

		@ $mol_mem
		documents_revision( next = 0 ) { return next }

		@ $mol_mem
		edit_document_id( next = '' ) { return next }

		manual_pages() {
			return [ this.Toc_page(), this.Text_page(), ...( this.editing() ? [ this.Editor( this.selected_document()!.id ) ] : [] ) ]
		}

		edit_enabled() { return Boolean( this.selected_document()?.editable ) }

		editing( next?: boolean ) {
			const doc = this.selected_document()
			if( next !== undefined ) {
				if( next && doc?.editable ) {
					const editor = this.Editor( doc.id )
					if( !editor.baseline() ) editor.load( doc as $shm_knowledge_manual_editor_document )
					this.edit_document_id( doc.id )
				} else this.edit_document_id( '' )
			}
			return Boolean( doc && this.edit_document_id() === doc.id )
		}

		editor_title( id: string ) { return `Редактирование: ${ this.documents().find( doc => doc.id === id )?.title ?? '' }` }

		document_saved( id: string, next?: { content: string, revision: string } | null ) {
			if( next ) {
				// All aliases of one physical file must refresh after saving.
				this.documents_revision( this.documents_revision() + 1 )
			}
			return next ?? null
		}

		document_param() { return `knowledge_${ this.kind() }_document` }
		heading_param() { return `knowledge_${ this.kind() }_heading` }

		selected_document() {
			const id = this.$.$mol_state_arg.value( this.document_param() )
			return this.documents().find( doc => doc.id === id ) ?? this.documents()[0]
		}

		text() {
			const doc = this.selected_document()
			return doc?.content || 'Нет материалов'
		}
		text_title() { return this.selected_document()?.title ?? this.menu_title() }

		@ $mol_mem_key
		document_book( id: string ) {
			const book = new this.$.$shm_hitalama_textbook()
			book.text = ()=> this.documents().find( doc => doc.id === id )?.content ?? ''
			return book
		}

		@ $mol_mem
		toc_items() {
			const documents = this.documents()
			const projects = documents.filter( doc => doc.id !== 'common' )
			const ids = new Set( projects.map( doc => doc.id ) )
			const visited = new Set<string>()
			const items: { id: string, title: string, level: number }[] = []
			const append = ( doc: $shm_knowledge_manual_document, depth: number )=> {
				if( visited.has( doc.id ) ) return
				visited.add( doc.id )
				const headers = this.document_book( doc.id ).toc_items()
				const common = doc.id === 'common'
				const first = headers[0]?.level === 1 ? headers[0] : null
				items.push(
					{ id: `${ doc.id }:root`, title: common ? 'Общие инструкции' : doc.title, level: depth },
					... headers.filter( item => item !== first ).map( item => ({
						id: `${ doc.id }:${ item.id }`, title: item.title,
						level: common ? Math.max( 2, item.level ) : depth + item.level,
					}) ),
				)
				for( const child of projects.filter( child => child.parent_id === doc.id ) ) append( child, depth + 1 )
			}
			for( const doc of documents.filter( doc => doc.id === 'common' ) ) append( doc, 1 )
			for( const doc of projects.filter( doc => !doc.parent_id || !ids.has( doc.parent_id ) ) ) append( doc, 1 )
			// Malformed legacy cycles must not hide projects or recurse forever.
			for( const doc of projects ) append( doc, 1 )
			return items
		}

		toc_link_content( id: string ) {
			const title = this.Toc_title( id )
			if( id === 'common:root' ) return [ this.Common_icon(), title ]
			return id.endsWith( ':root' ) ? [ this.Project_icon( id ), title ] : [ title ]
		}

		toc_expanded_key( id: string ) {
			return `$shm_knowledge_manual:${ this.api_url() }:${ this.kind() }:toc:${ id }`
		}

		@ $mol_mem
		override Toc() {
			const tree = super.Toc()
			tree.expanded_default = ()=> false
			tree.expanded_key = ( id: string )=> this.toc_expanded_key( id )
			return tree
		}

		toc_selected() {
			const document = this.$.$mol_state_arg.value( this.document_param() )
			const heading = this.$.$mol_state_arg.value( this.heading_param() )
			const doc = this.documents().find( doc => doc.id === document )
			if( !doc ) return ''
			if( !heading ) return `${ doc.id }:root`
			const book = this.document_book( doc.id )
			const item = book.toc_items().find( item => book.toc_anchor( item.id ) === heading )
			const id = item ? `${ doc.id }:${ item.id }` : ''
			return this.toc_items().some( item => item.id === id ) ? id : ''
		}

		toc_uri( id: string ) {
			return this.Toc_link( id ).uri()
		}

		toc_arg( id: string ) {
			const [ document, heading ] = id.split( ':' )
			return {
				[ this.document_param() ]: document,
				[ this.heading_param() ]: heading === 'root' ? null : this.document_book( document ).toc_anchor( heading ),
			}
		}

		@ $mol_action
		editor_close( event?: Event ) {
			if( !event ) return null
			this.editing( false )
			return event
		}

	}

}
